/**
 * Agora's adapter for the Work view (Phase 8b): the drawer on the right of any page, and the
 * room's card for what Claude waits on. The view itself is the standalone module in ../workview.
 */
import type { PromptAnswer } from "@agora/agent-stream";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { closeDrawer, EMPTY_EVENTS, loadEvents, openWorkFile, sendWork, setViewMode, useWork, useWorkNavigation, answerPrompt } from "../lib/work";
import { useStore, type ClaudePromptView } from "../lib/store";
import { PromptCard, WorkView } from "../workview";

export function WorkDrawer() {
  const navigate = useNavigate();
  useWorkNavigation(navigate);
  const sessionId = useWork((w) => w.drawer);
  const events = useWork((w) => (sessionId ? (w.events[sessionId] ?? EMPTY_EVENTS) : EMPTY_EVENTS));
  const session = useWork((w) => (sessionId ? w.sessions[sessionId] : undefined));
  const mode = useWork((w) => w.mode);
  const owner = useStore((s) => Boolean(s.boot?.me.isOwner));
  useEffect(() => {
    if (sessionId) void loadEvents(sessionId);
  }, [sessionId]);
  useEffect(() => {
    if (!sessionId) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && closeDrawer();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [sessionId]);
  if (!sessionId) return null;
  return (
    <aside className="work-drawer" aria-label="Claude's Work view">
      <WorkView
        layout="drawer"
        title={session?.title ?? "Claude"}
        events={events}
        live={session?.live}
        canDrive={owner}
        send={(cmd) => sendWork(sessionId, cmd)}
        openFile={(path) => openWorkFile(sessionId, path)}
        viewMode={mode}
        onViewMode={(m) => setViewMode(m, sessionId)}
        onClose={closeDrawer}
        actions={
          <Link className="wv-link" to={`/work/${sessionId}`} onClick={closeDrawer}>
            Full page
          </Link>
        }
      />
    </aside>
  );
}

/** The card in the thread: answered here, or in the Work view. */
export function RoomPromptCard({ prompt }: { prompt: ClaudePromptView }) {
  const owner = useStore((s) => Boolean(s.boot?.me.isOwner));
  const byName = useStore((s) => (prompt.decidedBy ? s.boot?.people.find((p) => p.id === prompt.decidedBy)?.name : undefined));
  const status = prompt.status === "open" ? "open" : (prompt.decision ?? "expired");
  const note = typeof prompt.answer?.note === "string" ? prompt.answer.note : undefined;
  const answers = prompt.answer && typeof prompt.answer.answers === "object" ? (prompt.answer.answers as Record<string, string>) : undefined;
  return (
    <div className="room-prompt">
      <PromptCard prompt={prompt.spec} status={status} by={byName} note={note} answers={answers} canAnswer={owner} compact onAnswer={(a: PromptAnswer) => answerPrompt(prompt.id, a)} />
      <Link className="wv-link" to={`/work/${prompt.sessionId}`}>
        Open the Work view
      </Link>
    </div>
  );
}
